/**
 * CommunityPickupCelebrator — runs invisibly inside the AppShell.
 *
 * Polls `/api/community/pickups` every 60s. For each unseen pickup
 * (another teacher imported one of the current teacher's shared
 * lessons), fires a confetti burst + a sonner toast, then POSTs the
 * pickup ids back as seen so the celebration never repeats.
 *
 * Why this lives outside the top bar: confetti has to spawn over the
 * whole viewport, and the toast surface is provided by the global
 * <Toaster />. Polling logic stays in one file instead of leaking
 * into every page.
 */
import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { fetchUnseenPickups, markPickupsSeen } from "../../lib/api";

const POLL_MS = 60_000;
const INITIAL_DELAY_MS = 2_500;

const _fireConfetti = () => {
  // Two slightly offset bursts so the spread looks organic rather
  // than a single dense cone. Mint + slate match the brand palette.
  const palette = ["#96ded1", "#1c2d3d", "#4a6984", "#ffffff"];
  confetti({
    particleCount: 80,
    spread: 70,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.25 },
    colors: palette,
    scalar: 0.9,
    ticks: 160,
  });
  window.setTimeout(() => {
    confetti({
      particleCount: 50,
      spread: 100,
      startVelocity: 35,
      origin: { x: 0.5, y: 0.3 },
      colors: palette,
      scalar: 0.8,
      ticks: 140,
    });
  }, 180);
};

const _celebrate = (pickup) => {
  _fireConfetti();
  toast.success(
    `Your "${pickup.source_activity_name}" just got picked up by another teacher!`,
    {
      description: "Nice work — it's helping a classroom somewhere right now.",
      duration: 7000,
    },
  );
};

export const CommunityPickupCelebrator = () => {
  const inFlightRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const data = await fetchUnseenPickups();
        if (!mounted) return;
        const pickups = Array.isArray(data?.pickups) ? data.pickups : [];
        if (pickups.length === 0) return;
        // Celebrate up to 3 per poll cycle (avoid back-to-back overload
        // if a popular lesson got imported five times in one minute).
        const toShow = pickups.slice(0, 3);
        toShow.forEach((p, i) => {
          window.setTimeout(() => _celebrate(p), i * 900);
        });
        // Mark ALL fetched pickups as seen so the queue stays drained.
        await markPickupsSeen(pickups.map((p) => p.id));
      } catch {
        // Free/anonymous teachers may hit auth gates — silently no-op.
      } finally {
        inFlightRef.current = false;
      }
    };
    const initial = window.setTimeout(tick, INITIAL_DELAY_MS);
    const poll = window.setInterval(tick, POLL_MS);
    return () => {
      mounted = false;
      window.clearTimeout(initial);
      window.clearInterval(poll);
    };
  }, []);

  return null;
};

export default CommunityPickupCelebrator;
